/**
 * O que a tela faz com o `GET /agentes` — puro, sem store e sem componente.
 *
 * Duas responsabilidades, e nenhuma delas é "saber quem pode escolher quem":
 * essa régua é do backend (`core/agentes/selecionaveis.py`), e duplicá-la aqui
 * garantiria divergência no primeiro agente novo.
 *
 * 1. **Degradar sem quebrar.** A rota só existe a partir do PR #1787; em
 *    `desenvolvimento` ela ainda responde 404, e `services/connection.js`
 *    devolve `{}` nesse caso (`return response.data || {}`). Sem seletor o
 *    composer continua funcionando exatamente como hoje — o run nasce no
 *    agente default, que é o comportamento atual.
 * 2. **Traduzir a recusa tipada.** O backend recusa a escolha com `motivo`
 *    NOMEADO em vez de cair no default em silêncio. O silêncio é justamente o
 *    defeito que esta ponte fecha: o consultor pediu uma simulação de fee fixo
 *    e ficou sem saber por que o agente não tinha a ferramenta.
 */

/** Os `CAMPOS_PUBLICOS` do produtor — a tela não inventa nenhum outro. */
export const CAMPOS_DO_AGENTE = ['slug', 'nome', 'descricao', 'pergunta_guia']

/**
 * Motivos da recusa, como o backend os nomeia
 * (`selecionaveis.MOTIVO_*`). Copiados por VALOR porque são contrato de fio:
 * se o backend renomear um, o teste de contrato acusa.
 */
export const MOTIVO_DESCONHECIDO = 'agente_desconhecido'
export const MOTIVO_NAO_ROTEAVEL = 'agente_nao_roteavel'
export const MOTIVO_PAPEL = 'agente_inelegivel_para_o_papel'
export const MOTIVO_FUNCIONALIDADE = 'agente_sem_funcionalidade_contratada'

/**
 * ★ [merge desenvolvimento, 21/09/2026] Os três jeitos de o CATÁLOGO estar mal
 * formado quanto ao ponto de entrada (`rbac_do_catalogo.py:41-43`). Chegaram
 * pelo merge, e o drift-guard abaixo foi quem os denunciou: sem estas quatro
 * linhas, os três caem no texto padrão e o consultor lê "não foi possível
 * usar este especialista" para um problema de CONFIGURAÇÃO que só o gestor
 * resolve — a recusa sem saída que o comentário do `TEXTO_POR_MOTIVO` já
 * proibia.
 */
export const MOTIVO_SEM_PONTO_DE_ENTRADA = 'catalogo_sem_ponto_de_entrada'
export const MOTIVO_PONTO_DE_ENTRADA_AMBIGUO = 'catalogo_com_mais_de_um_ponto_de_entrada'
export const MOTIVO_ENTRADA_TAMBEM_ROTEAVEL = 'ponto_de_entrada_tambem_roteavel'

/**
 * A frase que o consultor lê quando o agente escolhido não vai atender.
 *
 * Cada uma diz o MOTIVO e o que fazer — mensagem de erro sem saída deixa o
 * consultor parado. Motivo desconhecido (backend novo, front velho) cai numa
 * frase honesta em vez de uma inventada.
 */
const TEXTO_POR_MOTIVO = {
  [MOTIVO_DESCONHECIDO]:
    'Este especialista não existe mais. Escolha outro na lista e envie de novo.',
  [MOTIVO_NAO_ROTEAVEL]:
    'Este especialista não atende conversas. Escolha outro na lista e envie de novo.',
  [MOTIVO_PAPEL]:
    'Este especialista é restrito a gestores. Escolha outro ou peça acesso ao gestor do escritório.',
  [MOTIVO_FUNCIONALIDADE]:
    'Seu escritório não tem este especialista contratado. Escolha outro ou fale com o suporte.',
  // Os três de CATÁLOGO: a saída não é do consultor, é do gestor — dizer
  // "escolha outro" aqui manda ele tentar o que não vai funcionar.
  [MOTIVO_SEM_PONTO_DE_ENTRADA]:
    'O catálogo de especialistas do seu escritório está sem porta de entrada. Avise o gestor: nenhum especialista atende até isso ser corrigido.',
  [MOTIVO_PONTO_DE_ENTRADA_AMBIGUO]:
    'O catálogo do seu escritório tem mais de uma porta de entrada, e por isso nenhuma vale. Avise o gestor.',
  [MOTIVO_ENTRADA_TAMBEM_ROTEAVEL]:
    'A porta de entrada do catálogo também está marcada como especialista, e as duas coisas se excluem. Avise o gestor.',
}

const TEXTO_PADRAO_DA_RECUSA =
  'Não foi possível usar este especialista. Escolha outro na lista e envie de novo.'

const ehAgenteUtilizavel = (agente) =>
  Boolean(agente && agente.slug && agente.nome)

/**
 * A lista de agentes de um envelope de `listarSelecionaveis()`.
 *
 * SEMPRE um array: envelope de erro, rota inexistente (`{}`), `value` que não
 * é lista e item sem `slug`/`nome` viram lista vazia ou são descartados. Lista
 * vazia é um estado legítimo — escritório sem RBAC de nenhum roteável — e a
 * tela trata igual a "não deu para buscar": sem seletor.
 */
export function agentesDaResposta(resposta) {
  if (!resposta || resposta.status !== 200) return []
  if (!Array.isArray(resposta.value)) return []
  return resposta.value.filter(ehAgenteUtilizavel)
}

/**
 * O item como `agent-select.vue` o consome: ele chaveia por `id`, e o
 * contrato do backend chama isso de `slug`. A tradução mora aqui, num lugar
 * só — não espalhada pelos consumidores.
 *
 * Sem `iconName`: o contrato do produtor não traz ícone, e `icon-svg` com
 * nome inexistente não dá erro, simplesmente não desenha. Inventar um nome
 * aqui produziria um seletor de itens invisíveis.
 */
export function paraItemDeSelecao(agente) {
  return {
    id: agente.slug,
    slug: agente.slug,
    // `label`/`shortName`/`description`/`iconName` são os nomes que
    // `agent-select.vue` LÊ de cada item (`opcoes()`, `rotuloChip`,
    // `iconeChip`) — não os que seriam naturais em PT-BR. Escrever
    // `descricao` aqui deixaria a legenda de cada item em branco, sem erro
    // nenhum; e sem `shortName` o chip fica sem rótulo.
    label: agente.nome,
    shortName: agente.nome,
    description: agente.descricao || '',
    perguntaGuia: agente.pergunta_guia || '',
  }
}

/**
 * O item "Automático" que ABRE a lista — e que representa NÃO ter escolhido.
 *
 * Existe por um detalhe do componente que muda o produto: `agent-select.vue::
 * agenteAtivo` cai no **primeiro da lista** quando nenhum item casa com o id
 * ativo. Sem este item, o chip anunciaria "Especialista em Fee Fixo" (o
 * primeiro por ordem alfabética do backend) enquanto o corpo do aceite não
 * mandaria `agent_slug` nenhum e o run nasceria no agente default: a tela
 * mentindo sobre quem vai responder.
 *
 * A alternativa seria já nascer com o primeiro especialista escolhido — mas
 * isso mudaria o default de TODOS, e a ordem é que o default continua o
 * de hoje até o generalista da #171 existir.
 */
export const ID_AUTOMATICO = '__automatico__'

export const ITEM_AUTOMATICO = {
  id: ID_AUTOMATICO,
  slug: '',
  label: 'Automático',
  shortName: 'Automático',
  description: 'A plataforma escolhe o especialista para este turno.',
  perguntaGuia: '',
}

/** Todos os itens de seleção de um envelope, na ordem que o backend mandou. */
export function itensDeSelecaoDaResposta(resposta) {
  return agentesDaResposta(resposta).map(paraItemDeSelecao)
}

/**
 * A recusa da escolha veio no envelope do `POST /runs`?
 *
 * O backend responde 400 (slug desconhecido/não roteável) ou 403 (fora do
 * RBAC) com `value = {motivo, agent_slug}`. Só é recusa DE AGENTE quando o
 * `motivo` está entre os nomeados — um 403 de outra causa (sem a
 * funcionalidade `IA - Agente`, por exemplo) não é isto e não pode ser
 * traduzido como se fosse.
 */
export function recusaDeAgente(resposta) {
  const valor = (resposta && resposta.value) || null
  if (!valor || !valor.motivo) return null
  if (!Object.hasOwn(TEXTO_POR_MOTIVO, valor.motivo)) return null
  return { motivo: valor.motivo, agentSlug: valor.agent_slug || '' }
}

/** A frase a mostrar para uma recusa já identificada por `recusaDeAgente`. */
export function textoDaRecusa(motivo) {
  return TEXTO_POR_MOTIVO[motivo] || TEXTO_PADRAO_DA_RECUSA
}
